import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import BackgroundShapes from '../common/BackgroundShapes';
import { getPageUrl } from '../../utils/urlHelpers';
import { condoService } from '../../services/condoService';

// Login page URL configuration
export const loginUrl = getPageUrl('/login');

interface LoginPageProps {
  onNavigate: (page: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onNavigate }) => {
  const { login, loginLoading, getRedirectPath } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showPassword, setShowPassword] = useState(false);

  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear general error when user makes any change
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
    
    // Real-time validation for specific fields
    if (field === 'usernameOrEmail' && typeof value === 'string') {
      validateField(field, value);
    } else if (field === 'password' && typeof value === 'string') {
      validateField(field, value);
    }
  };

  const validateField = (field: string, value: string) => {
    const newErrors: {[key: string]: string} = {};
    
    if (field === 'usernameOrEmail') {
      if (!value.trim()) {
        newErrors.usernameOrEmail = 'Email or username required';
      } else {
        // Basic validation for email format if it contains @
        if (value.includes('@') && !/\S+@\S+\.\S+/.test(value)) {
          newErrors.usernameOrEmail = 'Invalid email format';
        }
        // Basic validation for username (3-30 characters, alphanumeric and underscores)
        else if (!value.includes('@') && !/^[a-zA-Z0-9_]{3,30}$/.test(value)) {
          newErrors.usernameOrEmail = 'Username must be 3-30 characters (letters, numbers, underscores)';
        }
      }
    } else if (field === 'password') {
      if (!value) {
        newErrors.password = 'Password required';
      } else if (value.length < 6) {
        newErrors.password = 'Password must be 6+ characters';
      }
    }
    
    // Only update errors for this specific field
    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = 'Email or username required';
    } else {
      // Basic validation for email format if it contains @
      if (formData.usernameOrEmail.includes('@') && !/\S+@\S+\.\S+/.test(formData.usernameOrEmail)) {
        newErrors.usernameOrEmail = 'Invalid email format';
      }
      // Basic validation for username (3-30 characters, alphanumeric and underscores)
      else if (!formData.usernameOrEmail.includes('@') && !/^[a-zA-Z0-9_]{3,30}$/.test(formData.usernameOrEmail)) {
        newErrors.usernameOrEmail = 'Username must be 3-30 characters (letters, numbers, underscores)';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be 6+ characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      console.log('Attempting login with:', formData.usernameOrEmail);
      const result = await login(formData.usernameOrEmail, formData.password);
      
      if (result.success) {
        // Clear any previous errors
        setErrors({});
        
        // Handle remember me functionality
        if (formData.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberMe');
        }
        
        // Get updated user from auth hook (will be set by login function)
        // Wait a moment for state to update, then redirect
        const navigateFn = navigate;
        const getRedirectPathFn = getRedirectPath;
        setTimeout(() => {
          const pendingCondoId = localStorage.getItem('pendingWaitlistCondoId');
          if (pendingCondoId) {
            localStorage.removeItem('pendingWaitlistCondoId');
            condoService.joinWaitlist(pendingCondoId).catch(() => {});
          }

          const savedUser = localStorage.getItem('userData');
          if (savedUser) {
            try {
              const parsedUser = JSON.parse(savedUser);
              // Check if admin, redirect accordingly
              if (parsedUser.role === 'admin') {
                navigateFn('/admin/dashboard', { replace: true });
              } else {
                // Use getRedirectPath for regular users
                const redirectPath = getRedirectPathFn(parsedUser);
                navigateFn(redirectPath, { replace: true });
              }
            } catch (error) {
              // Fallback to old navigation
              onNavigate('dashboard');
            }
          } else {
            // Fallback to old navigation
            onNavigate('dashboard');
          }
        }, 100);
      } else {
        setErrors({ general: result.error || 'Login failed' });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setErrors({ general: error.message || 'Login failed. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-coffee mb-2">Welcome Back</h1>
            <p className="text-coffee opacity-80">Sign in to your ShiYou account</p>
          </div>

          {/* Login Form */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-md">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg">
                  {errors.general}
                </div>
              )}

              {/* Username/Email Field */}
              <div>
                <label htmlFor="usernameOrEmail" className="block text-coffee text-sm font-medium mb-2">
                  Email or Username
                </label>
                <input
                  type="text"
                  id="usernameOrEmail"
                  value={formData.usernameOrEmail}
                  onChange={(e) => updateField('usernameOrEmail', e.target.value)}
                  placeholder="Enter your email or username"
                  className={`w-full px-4 py-3 rounded-lg bg-white border ${
                    errors.usernameOrEmail 
                      ? 'border-red-400' 
                      : formData.usernameOrEmail.trim() && !errors.usernameOrEmail
                        ? 'border-green-500'
                        : 'border-coffee'
                  } text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors`}
                  disabled={loginLoading}
                />
                {errors.usernameOrEmail && (
                  <p className="text-red-600 text-sm mt-1">{errors.usernameOrEmail}</p>
                )}
                {formData.usernameOrEmail.trim() && !errors.usernameOrEmail && (
                  <p className="text-green-600 text-sm mt-1">✓ Valid email/username</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-coffee text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    placeholder="Enter your password"
                    className={`w-full px-4 py-3 pr-12 rounded-lg bg-white border ${
                      errors.password 
                        ? 'border-red-400' 
                        : formData.password && !errors.password
                          ? 'border-green-500'
                          : 'border-coffee'
                    } text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors`}
                    disabled={loginLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-coffee hover:text-terracotta transition-colors"
                    disabled={loginLoading}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-sm mt-1">{errors.password}</p>
                )}
                {formData.password && !errors.password && (
                  <p className="text-green-600 text-sm mt-1">✓ Password is valid</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center text-coffee text-sm">
                  <input
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={(e) => updateField('rememberMe', e.target.checked)}
                    className="mr-2 rounded border-coffee bg-white text-terracotta focus:ring-terracotta"
                    disabled={loginLoading}
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  className="text-coffee hover:text-terracotta text-sm transition-colors"
                  disabled={loginLoading}
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-terracotta text-white py-3 rounded-lg font-bold text-lg hover:bg-[#d89050] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center border-2 border-coffee shadow-lg"
              >
                {loginLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>

            </form>
          </div>

          {/* Navigation Links */}
          <div className="text-center mt-6 space-y-2">
            <p className="text-coffee">
              Don't have an account?{' '}
              <button
                onClick={() => onNavigate('signup')}
                className="text-coffee hover:text-terracotta font-medium transition-colors underline decoration-terracotta"
                disabled={loginLoading}
              >
                Sign up here
              </button>
            </p>
            <button
              onClick={() => onNavigate('landing')}
              className="text-coffee hover:text-terracotta text-sm transition-colors"
              disabled={loginLoading}
            >
              ← Back to home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
